import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react"

import { Result } from "../../../../api/geolocation/get/types"
import { api } from "../../../../api"
import { t } from "@lingui/macro"

interface GeolocationContextType {
  data: Result | null
  errorLocation: string
  loading: boolean
  fetchGeolocation: (code: string, stateId?: string) => Promise<void>
  resetGeolocation: () => void
}

const GeolocationContext = createContext<GeolocationContextType | undefined>(
  undefined
)

export const GeolocationProvider = ({ children }: { children: ReactNode }) => {
  const [data, setData] = useState<Result | null>(null)
  const [errorLocation, setErrorLocation] = useState("")
  const [loading, setLoading] = useState(false)

  const fetchGeolocation = useCallback(
    async (code: string | null, stateId?: string) => {
      if (!code) {
        setData(null)

        setErrorLocation("")

        return
      }
      if (loading) return

      setLoading(true)
      setData(null)

      try {
        const params: any = { code }
        if (stateId) params.state_id = stateId

        const result = await api.geolocation.get(params)
        if (!result) throw new Error("No data returned from API")

        setData(result)
        setErrorLocation("")
      } catch (error) {
        setErrorLocation(
          t`No pudimos encontrar el código postal. Revisá e intentá otra vez.`
        )

        setData(null)
      } finally {
        setLoading(false)
      }
    },
    [loading]
  )

  const resetGeolocation = useCallback(() => {
    setData(null)
    setErrorLocation("")
  }, [])

  return (
    <GeolocationContext.Provider
      value={{
        data,
        errorLocation,
        loading,
        fetchGeolocation,
        resetGeolocation,
      }}
    >
      {children}
    </GeolocationContext.Provider>
  )
}

export const useGeolocation = () => {
  const context = useContext(GeolocationContext)
  if (!context) {
    throw new Error(
      "useGeolocation debe ser usado dentro de GeolocationProvider"
    )
  }
  return context
}
